import { ReferralResultsLoader } from "./ReferralResultsLoader";
import {
  ReferralConnectionBadge,
  ReferralContactDetails,
  ReferralFirmIdentity,
  ReferralPracticeAreas,
  ReferralServiceStates,
} from "./ReferralFirmCells";
import { useUser } from "@/components/UserProvider";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { useCreatePartnerRequest } from "@/lib/data/react-query/firms/referral-partners";
import {
  ActionEmptyState,
  AppPageShell,
  Button,
  Input,
  LoadingSpinner,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui";
import { QuickFilter } from "@/components/ui/FilterBar/FilterBar";
import { referralDirectoryQueryOptions } from "@/lib/data/react-query/firms/referral-profile";
import { retrieveIncidentTypeCategoriesQueryOptions } from "@/lib/data/react-query/matters/matter-type-category";
import { US_STATES } from "@/lib/utils/states";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";

export function ReferralDirectory({
  embedded = false,
}: {
  embedded?: boolean;
}) {
  const Wrapper = embedded ? Fragment : AppPageShell;
  const { hasActionPermission } = useUser();
  const canRequest = hasActionPermission(
    ACTION_PERMISSION_KEYS.firmReferralConnectionRequest,
    ACTION_PERMISSION_VALUES.create,
  );
  const createRequest = useCreatePartnerRequest();
  const [search, setSearch] = useState("");
  const [practiceAreaIds, setPracticeAreaIds] = useState<string[]>([]);
  const [serviceAreas, setServiceAreas] = useState<string[]>([]);
  const debouncedSearch = useDebouncedValue(search, 250);
  const categories = useQuery(retrieveIncidentTypeCategoriesQueryOptions());
  const hasSearchCriteria = Boolean(
    search.trim() || practiceAreaIds.length || serviceAreas.length,
  );
  const hasDebouncedCriteria = Boolean(
    debouncedSearch.trim() || practiceAreaIds.length || serviceAreas.length,
  );
  const directory = useInfiniteQuery({
    ...referralDirectoryQueryOptions({
      search: debouncedSearch.trim() || undefined,
      practiceAreaIds,
      serviceAreas,
    }),
    enabled: hasSearchCriteria && hasDebouncedCriteria,
  });
  const firms = directory.data?.pages.flatMap((page) => page.data) ?? [];
  return (
    <Wrapper>
      <div
        className={
          embedded ? "flex h-full min-h-0 flex-col gap-4 p-6" : "space-y-4"
        }
      >
        {!embedded && (
          <h1 className="text-xl font-semibold">Find referral firms</h1>
        )}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Input
            aria-label="Firm name"
            className="h-8 w-full sm:w-64"
            maxLength={200}
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            placeholder="Search firms..."
          />
          <QuickFilter
            filterOption={{
              label: `Practice areas${practiceAreaIds.length ? ` (${practiceAreaIds.length})` : ""}`,
              value: "practiceAreaIds",
              input: {
                type: "searchable-select",
                items: (categories.data?.data ?? []).map((item) => ({
                  value: item.id,
                  label: item.label,
                })),
              },
            }}
            selectedValues={practiceAreaIds}
            onAddFilter={(_key, value) => {
              setPracticeAreaIds((current) => [
                ...new Set([...current, value]),
              ]);
            }}
            onRemoveFilter={(_key, value) => {
              setPracticeAreaIds((current) =>
                current.filter((item) => item !== value),
              );
            }}
          />
          <QuickFilter
            filterOption={{
              label: `Service states${serviceAreas.length ? ` (${serviceAreas.length})` : ""}`,
              value: "serviceAreas",
              input: {
                type: "searchable-select",
                items: US_STATES.map((state) => ({
                  value: state,
                  label: state,
                })),
              },
            }}
            selectedValues={serviceAreas}
            onAddFilter={(_key, value) => {
              setServiceAreas((current) => [...new Set([...current, value])]);
            }}
            onRemoveFilter={(_key, value) => {
              setServiceAreas((current) =>
                current.filter((item) => item !== value),
              );
            }}
          />
          {hasSearchCriteria && (
            <Button
              variant="ghost"
              className="h-8 rounded-full px-3 text-xs"
              onClick={() => {
                setSearch("");
                setPracticeAreaIds([]);
                setServiceAreas([]);
              }}
            >
              Clear
            </Button>
          )}
        </div>
        {categories.isError && (
          <p role="alert" className="text-sm">
            Practice areas could not be loaded.{" "}
            <Button
              variant="outline"
              onClick={() => {
                void categories.refetch();
              }}
            >
              Retry
            </Button>
          </p>
        )}
        <div
          className={
            embedded
              ? "min-h-0 flex-1 space-y-4 overflow-auto overscroll-contain pr-3 pb-3 [scrollbar-gutter:stable]"
              : "space-y-4"
          }
        >
          {!hasSearchCriteria ? (
            <ActionEmptyState
              align="left"
              className="gap-2 p-4 [&_.text-xl]:text-base"
              title="Find a referral firm"
              description="Enter a firm name or choose a filter to get started."
            />
          ) : directory.isPending ||
            search.trim() !== debouncedSearch.trim() ? (
            <div
              role="status"
              className="text-muted-foreground flex items-center gap-2 rounded-xl border p-4 text-sm"
            >
              <LoadingSpinner /> Loading firms...
            </div>
          ) : directory.isError && !directory.isFetchNextPageError ? (
            <div role="alert">
              <ActionEmptyState
                align="left"
                className="gap-2 p-4 [&_.text-xl]:text-base"
                title="Could not load firms"
                description="Please try again."
                actions={
                  <Button
                    variant="outline"
                    onClick={() => {
                      void directory.refetch();
                    }}
                  >
                    Retry
                  </Button>
                }
              />
            </div>
          ) : firms.length === 0 ? (
            <ActionEmptyState
              align="left"
              className="gap-2 p-4 [&_.text-xl]:text-base"
              title="No firms found"
              description="Try another name or adjust your filters."
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border">
              <Table className="min-w-[920px]">
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead>Firm</TableHead>
                    <TableHead>Practice areas</TableHead>
                    <TableHead>Service states</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Connection</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {firms.map((firm) => (
                    <TableRow key={firm.firmId}>
                      <TableCell className="max-w-72 min-w-48 py-3">
                        <ReferralFirmIdentity name={firm.name} />
                      </TableCell>
                      <TableCell className="max-w-60 min-w-36 py-3">
                        <ReferralPracticeAreas areas={firm.practiceAreas} />
                      </TableCell>
                      <TableCell className="max-w-48 min-w-32 py-3">
                        <ReferralServiceStates states={firm.serviceAreas} />
                      </TableCell>
                      <TableCell className="max-w-72 min-w-56 py-3">
                        <ReferralContactDetails firm={firm} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {firm.relationshipStatus === "partner" ? (
                          <ReferralConnectionBadge status="partner" />
                        ) : firm.relationshipStatus === "incoming" ? (
                          <ReferralConnectionBadge status="incoming" />
                        ) : firm.relationshipStatus === "outgoing" ? (
                          <ReferralConnectionBadge status="outgoing" />
                        ) : canRequest ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={
                              createRequest.isPending &&
                              createRequest.variables === firm.firmId
                            }
                            onClick={() => {
                              createRequest.mutate(firm.firmId);
                            }}
                          >
                            {createRequest.isPending &&
                            createRequest.variables === firm.firmId
                              ? "Sending..."
                              : "Send request"}
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {hasSearchCriteria && (
            <ReferralResultsLoader
              hasNextPage={Boolean(directory.hasNextPage)}
              isFetching={directory.isFetching}
              isError={directory.isError}
              fetchNextPage={directory.fetchNextPage}
            />
          )}
        </div>
      </div>
    </Wrapper>
  );
}
